import { Component, Element, Event, EventEmitter, JSX, Prop, State, Watch, h } from '@stencil/core'

import { AlertVariant } from './air-alert.types'
import { isUndefined } from '../../utils/helpers'

@Component({
  tag: 'air-alert',
  styleUrl: 'air-alert.scss',
  shadow: true,
})
export class AirAlert {
  /** Reference to the host element */
  @Element() host!: HTMLAirAlertElement

  /** Alert text */
  @Prop() text?: string

  /** Specify the color applied to the borders and icon */
  @Prop({ reflect: true, mutable: true }) variant: AlertVariant = 'info'

  @Watch('variant')
  watchVariant(newValue: AlertVariant): void {
    this.variant = this.handleVariantFallback(newValue)
  }

  /**
   * Specifies what icon from asset library is shown
   * Default icon will be rendered accordingly to the variant
   */
  @Prop() icon?: string

  /**
   * Specifies what icon from asset library is shown for dismissing the alert
   */
  @Prop() iconDismiss = 'x'

  /** Specifies if the close icon is shown */
  @Prop() dismissible = false

  /** Specifies if the alert is shown */
  @State() visible = true

  /** Emits the air-dismiss event */
  @Event({ eventName: 'air-dismiss' }) dismiss!: EventEmitter<void>

  componentWillLoad(): void {
    this.variant = this.handleVariantFallback(this.variant)
  }

  /** Dismiss the alert */
  private dismissAlert = (): void => {
    this.visible = false
    this.dismiss.emit()
  }

  /** Get icon by the variant */
  private getIconNameByVariant(variant: AlertVariant): string {
    const iconsByVariant = {
      warning: 'alert-triangle',
      danger: 'slash',
      success: 'check-circle',
      info: 'alert-circle',
    }
    return iconsByVariant[variant] ?? 'alert-circle'
  }

  private handleVariantFallback(value: AlertVariant): AlertVariant {
    const list: AlertVariant[] = ['info', 'danger', 'success', 'warning']
    return list.includes(value) ? value : list[0]
  }

  private renderIcon(): JSX.Element | null {
    if (isUndefined(this.icon) || this.icon === '') {
      return <air-icon class="alert-icon" name={this.getIconNameByVariant(this.variant)}></air-icon>
    }
    return <air-icon class="alert-icon" name={this.icon}></air-icon>
  }

  private renderDismissIcon(): JSX.Element | null {
    if (isUndefined(this.iconDismiss) || this.iconDismiss === '') {
      return <air-icon name="x"></air-icon>
    }
    return <air-icon name={this.iconDismiss}></air-icon>
  }

  render(): JSX.Element {
    const { variant, text, dismissible, visible } = this
    const alertClasses = {
      [`alert`]: true,
      [`hidden`]: !visible,
      [`alert-${variant}`]: true,
      [`dismissible`]: !!dismissible,
    }

    return (
      <div aria-hidden={!visible} hidden={!visible} class={alertClasses} role="alert">
        {this.renderIcon()}
        <div class="alert-content">
          {text && <span class="alert-heading">{text}</span>}
          <slot />
        </div>
        {dismissible && (
          <div class="alert-close-button-wrapper">
            <span
              role="button"
              class="alert-close-button"
              aria-label="close"
              onClick={this.dismissAlert}
            >
              {this.renderDismissIcon()}
            </span>
          </div>
        )}
      </div>
    )
  }
}
